import { useQuery } from '@tanstack/react-query'
import { Pencil } from 'lucide-react'
import type { ReactNode } from 'react'

import type { PartySummary } from '@aeris/contracts'
import { MONEY_SCALE, formatMoney, fromString } from '@aeris/money'
import { formatDocument, partyDisplayName } from '@aeris/party'
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@aeris/ui'

import { fetchCustomerCredit } from '../lib/api/exchanges.ts'
import { useAuth } from '../lib/auth-context.ts'
import { PARTY_ROLE_LABELS } from '../lib/party-labels.ts'

import { PartyCreditPanel } from './party-credit-panel.tsx'
import { PartyHistory } from './party-history.tsx'
import { PartyIdentity } from './party-identity.tsx'
import { PartyVoucherPanel } from './party-voucher-panel.tsx'

const money = (wire: string): string =>
  formatMoney(fromString(wire, MONEY_SCALE))

/**
 * A ficha do cliente — F151.
 *
 * Clicar na linha abre isto, e não o formulário de cadastro. A diferença que
 * importa está no HISTÓRICO: quem abre um cliente no balcão quase sempre quer
 * saber o que ele comprou e o que deve, e isso o formulário de edição mostrava
 * junto de vinte campos editáveis.
 *
 * O histórico é o `PartyHistory` que já existe — ele já é só leitura, já recebe
 * `partyId` e já decide sozinho o que mostrar conforme `sales.read` e
 * `finance.read`.
 *
 * F208: o vale e o crédito também moram aqui, em abas — antes só apareciam
 * dentro do formulário de edição, e o dono quer "tudo" na ficha: de onde veio
 * cada vale, onde foi usado, e o que sobra.
 */

export const PartyDetailDialog = ({
  party,
  onClose,
  onEdit,
}: {
  party: PartySummary
  onClose: () => void
  /** Só quem tem `party.write`. Ausente, o botão não existe. */
  onEdit?: (() => void) | undefined
}): ReactNode => {
  const { allows } = useAuth()
  const canReadVoucher = allows('exchange.read')
  const canReadCredit = allows('finance.read')

  /*
   * O saldo no cabeçalho (F208): quem abre a ficha no balcão quer saber, antes
   * de tudo, se o cliente tem vale. É a mesma consulta que o PDV faz.
   */
  const voucher = useQuery({
    queryKey: ['customer-credit', party.id],
    queryFn: async ({ signal }) => await fetchCustomerCredit(party.id, signal),
    enabled: canReadVoucher,
  })
  const balance = voucher.data?.balance ?? '0.00'

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        title={partyDisplayName(party)}
        description="Conferir o cliente. Nada muda nesta tela: alterar é pelo botão Editar."
        // Código e telefone com copiar, e o WhatsApp a um clique (F248).
        meta={<PartyIdentity party={party} />}
        className="max-w-3xl"
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            {party.status === 'active' ? null : (
              <Badge tone="neutral">Arquivado</Badge>
            )}
            {party.roles.map((role) => (
              <Badge key={role} tone="info">
                {PARTY_ROLE_LABELS[role]}
              </Badge>
            ))}
            {balance === '0.00' ? null : (
              <Badge tone="success">Vale {money(balance)}</Badge>
            )}
          </div>

          <Tabs defaultValue="summary" className="flex flex-col gap-3">
            <TabsList className="self-start">
              <TabsTrigger value="summary">Resumo</TabsTrigger>
              {canReadVoucher ? (
                <TabsTrigger value="voucher">Vale</TabsTrigger>
              ) : null}
              {canReadCredit ? (
                <TabsTrigger value="credit">Crédito</TabsTrigger>
              ) : null}
            </TabsList>

            <TabsContent value="summary" className="flex flex-col gap-4">
              <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-(length:--text-sm)">
                <dt className="text-(--text-muted)">
                  {party.kind === 'person' ? 'Nome' : 'Razão social'}
                </dt>
                <dd className="text-(--text-primary)">{party.legalName}</dd>
                <dt className="text-(--text-muted)">CPF/CNPJ</dt>
                <dd className="font-(--font-mono) text-(--text-primary)">
                  {party.document === null
                    ? '—'
                    : formatDocument(party.kind, party.document)}
                </dd>
                {/*
                  O código, o código antigo e o telefone moram no cabeçalho,
                  com copiar (F248). Aqui, o resto do que o balcão procura — a
                  ficha não trazia nem o e-mail, nem a cidade, nem de quem é o
                  cliente, embora tudo já viesse com ele.
                */}
                {party.mainEmail == null ? null : (
                  <>
                    <dt className="text-(--text-muted)">E-mail</dt>
                    <dd className="break-all text-(--text-primary)">
                      {party.mainEmail}
                    </dd>
                  </>
                )}
                {party.mainCity == null ? null : (
                  <>
                    <dt className="text-(--text-muted)">Cidade</dt>
                    <dd className="text-(--text-primary)">
                      {party.mainUf == null
                        ? party.mainCity
                        : `${party.mainCity} – ${party.mainUf}`}
                    </dd>
                  </>
                )}
                {party.salespersonName == null ? null : (
                  <>
                    {/* F210: a carteira. */}
                    <dt className="text-(--text-muted)">Vendedor</dt>
                    <dd className="text-(--text-primary)">
                      {party.salespersonName}
                    </dd>
                  </>
                )}
                {party.notes === null ? null : (
                  <>
                    <dt className="text-(--text-muted)">Observação</dt>
                    <dd className="text-(--text-primary)">{party.notes}</dd>
                  </>
                )}
              </dl>

              <PartyHistory partyId={party.id} />
            </TabsContent>
            {canReadVoucher ? (
              <TabsContent value="voucher">
                <PartyVoucherPanel
                  partyId={party.id}
                  customerName={party.legalName}
                />
              </TabsContent>
            ) : null}
            {canReadCredit ? (
              <TabsContent value="credit">
                <PartyCreditPanel partyId={party.id} canManage={false} />
              </TabsContent>
            ) : null}
          </Tabs>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Fechar
          </Button>
          {onEdit === undefined ? null : (
            <Button
              variant="primary"
              leadingIcon={<Pencil aria-hidden="true" className="h-4 w-4" />}
              onClick={onEdit}
            >
              Editar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
